import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { categoryPath } from "../../lib/category-slugs";

/**
 * FooterV2 — compact B2B business footer.
 *
 * Previous version led with a giant decorative wordmark (clamp width up
 * to 38rem). That looked premium on a portfolio site but felt empty and
 * "showcase" on a B2B grossiste site. Replaced with:
 *   - Top row: compact logo + brand kicker + address inline (≈ business card)
 *   - KPI strip: 4 hard numbers (300+ refs, 13 gammes, 23 marques, depuis 2009)
 *   - Catalogue CTA strip: kept, slightly tighter padding
 *   - Link cols: 3 (Catalogue / Concept / Société) — Legal demoted to bottom
 *     bar to save a column slot and densify the grid
 *   - Bottom bar: copyright + legal links + address line, single line desktop
 *
 * Result: same information, ~30% less vertical space, no more "portfolio"
 * vibe. Same brand assets, no new dependency.
 *
 * Category links use the new /catalogue/<url-slug> paths (see
 * lib/category-slugs.js). Legacy `?categorie=` URLs still resolve.
 */
export const FooterV2 = () => {
  const year = new Date().getFullYear();

  const KPIS = [
    { value: "300+", label: "Références" },
    { value: "13", label: "Gammes" },
    { value: "23", label: "Marques sélectionnées" },
    { value: "2009", label: "Depuis" },
  ];

  const cols = [
    {
      title: "Catalogue",
      links: [
        { to: categoryPath("frites"), label: "Frites surgelées" },
        { to: categoryPath("sauce"), label: "Sauces" },
        { to: categoryPath("viandes"), label: "Viandes" },
        { to: categoryPath("volailles"), label: "Volailles" },
        { to: categoryPath("emballages"), label: "Emballages pro" },
        { to: "/catalogue", label: "Voir les 13 gammes →" },
        { to: "/galerie", label: "Galerie produits" },
      ],
    },
    {
      title: "Concept",
      links: [
        { to: "/concept/franchises-multi-sites", label: "Franchises & multi-sites" },
        { to: "/concept/frites-signature", label: "Frite signature" },
        { to: "/concept/kebab", label: "Kebab" },
        { to: "/concept/burger", label: "Burger" },
        { to: "/concept/tacos", label: "Tacos" },
      ],
    },
    {
      title: "Société",
      links: [
        { to: "/a-propos", label: "À propos" },
        { to: "/blog", label: "Journal" },
        { to: "/faq", label: "FAQ B2B" },
        { to: "/contact", label: "Contact commercial" },
      ],
    },
  ];

  return (
    <footer data-testid="footer-v2" className="relative bg-fd-bg border-t border-fd-border pt-14 lg:pt-16 pb-24 lg:pb-12 px-6 md:px-10 lg:px-14 overflow-hidden">
      {/* Identity row — logo inline + address card. Compact, business. */}
      <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 lg:gap-12 mb-10 lg:mb-12">
        <div className="flex items-start gap-5 max-w-xl">
          <Link
            to="/"
            aria-label="Frites Dorées — accueil"
            className="shrink-0"
          >
            <picture>
              <source srcSet="/brand/logo-frites-dorees.webp" type="image/webp" />
              <img
                src="/brand/logo-frites-dorees.png"
                alt="Frites Dorées"
                width={80}
                height={80}
                className="h-16 sm:h-20 w-auto object-contain select-none"
                draggable="false"
              />
            </picture>
          </Link>
          <div>
            <p className="mono-label-gold mb-2">Frites Dorées · Grossiste B2B</p>
            <p className="font-body text-sm text-fd-text/70 leading-relaxed max-w-md">
              Grossiste moderne pour la restauration rapide. Sourcing pro, accompagnement commercial, solutions multi-sites et livraison professionnels en Île-de-France. Labellisé BPI France Excellence.
            </p>
          </div>
        </div>

        {/* Direct contact card — never more than 1 tap away in the footer */}
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:flex lg:flex-col gap-4 lg:gap-2 lg:items-end shrink-0">
          <a
            href="tel:+33148680303"
            className="flex items-center gap-2 font-display text-sm md:text-base text-fd-text hover:text-fd-gold transition-colors"
            data-testid="footer-tel"
          >
            <Phone size={14} className="text-fd-gold" /> 01 48 68 03 03
          </a>
          <a
            href="mailto:contact@fritesdorees.fr"
            className="flex items-center gap-2 font-body text-sm text-fd-text/80 hover:text-fd-gold transition-colors"
            data-testid="footer-mail"
          >
            <Mail size={14} className="text-fd-gold" /> contact@fritesdorees.fr
          </a>
          <p className="flex items-center gap-2 font-body text-sm text-fd-text/60">
            <MapPin size={14} className="text-fd-gold" /> 55-73 rue Blaise Pascal, 93600 Aulnay-sous-Bois
          </p>
        </div>
      </div>

      {/* KPI strip — 4 hard numbers, no marketing fluff */}
      <div className="max-w-[1400px] mx-auto mb-10 lg:mb-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-fd-border border-y border-fd-border" data-testid="footer-kpis">
        {KPIS.map((k) => (
          <div key={k.label} className="bg-fd-bg px-4 py-5 lg:py-6 text-center md:text-left">
            <p className="font-display text-2xl md:text-3xl lg:text-4xl text-gradient-gold leading-none">{k.value}</p>
            <p className="mono-label !text-[10px] mt-2 !text-fd-text/65">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Catalogue lead CTA strip — tighter than before, same intent */}
      <div className="max-w-[1400px] mx-auto mb-10 lg:mb-12" data-testid="footer-catalog-cta">
        <div className="border border-fd-gold/25 bg-[radial-gradient(ellipse_at_top_left,rgba(212,162,76,0.08),transparent_60%)] p-5 lg:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div className="max-w-xl">
            <p className="mono-label-gold mb-2 flex items-center gap-3">
              <span className="w-6 h-px bg-fd-gold"></span>
              <span>Catalogue B2B · Devis 24h</span>
            </p>
            <p className="font-display text-lg md:text-2xl uppercase tracking-tight leading-tight text-fd-text">
              Feuilletez notre catalogue grossiste en ligne.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              to="/catalogue-feuilletable"
              data-cursor-hover
              data-testid="footer-catalog-request"
              className="btn-pill btn-primary-v2"
            >
              Voir le catalogue <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/contact"
              data-cursor-hover
              data-testid="footer-quote-cta"
              className="inline-flex items-center justify-center gap-2 px-6 py-[0.85rem] rounded-full border border-white/25 text-fd-text font-display font-medium uppercase tracking-[0.02em] text-[0.78rem] whitespace-nowrap hover:border-white transition-colors"
            >
              Devis commercial
            </Link>
          </div>
        </div>
      </div>

      {/* Link columns — 3 cols, denser, no orphan Legal column */}
      <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-10">
        {cols.map((col) => (
          <div key={col.title} className={col.title === "Société" ? "col-span-2 md:col-span-1" : ""}>
            <h3 className="mono-label-gold mb-4">{col.title}</h3>
            <ul className="space-y-2">
              {col.links.map((l, i) => (
                <li key={i}>
                  {l.href ? (
                    <a href={l.href} data-cursor-hover className="font-body text-sm text-fd-text/80 hover:text-fd-gold transition-colors">{l.label}</a>
                  ) : (
                    <Link to={l.to} data-cursor-hover className="font-body text-sm text-fd-text/80 hover:text-fd-gold transition-colors">{l.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Bottom bar — copyright + legal links collapsed here so they don't
          take a whole footer column. Single line on desktop, stacks on mobile. */}
      <div className="max-w-[1400px] mx-auto mt-12 pt-6 border-t border-fd-border flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <p className="mono-label !text-[10px]">© {year} Frites Dorées — Tous droits réservés · Paris · Aulnay-sous-Bois</p>
        <ul className="flex flex-wrap gap-x-5 gap-y-2">
          <li><Link to="/mentions-legales" className="mono-label !text-[10px] hover:text-fd-gold transition-colors">Mentions légales</Link></li>
          <li><Link to="/confidentialite" className="mono-label !text-[10px] hover:text-fd-gold transition-colors">Confidentialité</Link></li>
          <li><Link to="/cgu" className="mono-label !text-[10px] hover:text-fd-gold transition-colors">CGU</Link></li>
        </ul>
      </div>
    </footer>
  );
};
